/**
 * Super Admin — User segments (Active / New / Guests) + Retention table.
 * Reuses the same data API as Users.jsx with prefilled filters.
 */
import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../../services/api";
import { SectionHeader } from "./SuperadminApp";
import { TableShell, Th, Td, Card, EmptyState, relTime, fmtINR } from "./_atoms";

function UserList({ title, status, sort = "active", testid }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    apiClient.get("/superadmin/users", { params: { status, sort, page: 1, size: 100 } })
      .then((r) => { setUsers(r.data.users || []); setTotal(r.data.total || 0); });
  }, [status, sort]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid={testid} className="space-y-4">
      <SectionHeader title={title} onRefresh={load} />
      {users.length === 0 ? (
        <EmptyState title="No users found" sub="Try a different filter" />
      ) : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><Th>Name</Th><Th>Email</Th><Th>City</Th><Th>Last active</Th><Th>FitPoints</Th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id}>
                  <Td><span style={{ color: "var(--text-primary, #0C0C0B)", fontWeight: 600 }}>{u.name}</span></Td>
                  <Td>{u.email}</Td>
                  <Td>{u.city || "—"}</Td>
                  <Td>{relTime(u.last_active_at)}</Td>
                  <Td>{u.fitpoints_balance ?? 0}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>Showing {users.length} of {total}</div>
    </div>
  );
}

export function ActiveUsers() {
  return <UserList title="Active Today" status="active" sort="active" testid="users-active" />;
}
export function NewUsers() {
  return <UserList title="New Registrations" status="" sort="newest" testid="users-new" />;
}

export function GuestSessions() {
  const [data, setData] = useState({ sessions: [], total: 0 });
  const load = useCallback(() => {
    apiClient.get("/superadmin/users/guest-sessions").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="users-guests">
      <SectionHeader title="Guest Sessions" onRefresh={load} />
      <Card>
        <div style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>
          Total guest sessions tracked: <strong>{data.total}</strong>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>
          Guest sessions are stored only when the visitor performs an action (food scan, trainer browse, etc).
        </p>
      </Card>
      {data.sessions.length === 0 ? (
        <EmptyState title="No guest sessions" sub="Once visitors interact, they'll appear here." />
      ) : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><Th>Session ID</Th><Th>Created</Th><Th>Scans</Th><Th>Last action</Th></tr>
            </thead>
            <tbody>
              {data.sessions.map((s) => (
                <tr key={s.session_id}>
                  <Td><code style={{ fontSize: 11 }}>{s.session_id?.slice(0, 14)}…</code></Td>
                  <Td>{relTime(s.created_at)}</Td>
                  <Td>{s.scan_count ?? 0}</Td>
                  <Td>{s.last_action || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

export function Retention() {
  const [rows, setRows] = useState([]);
  const [trend, setTrend] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/users/retention/cohorts", { params: { weeks: 12 } })
      .then((r) => setRows(r.data?.cohorts || []));
    apiClient.get("/superadmin/users/retention/trend").then((r) => setTrend(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cell = (v) => {
    if (v == null) return <span style={{ color: "var(--text-tertiary, #8A8884)" }}>—</span>;
    const intensity = Math.min(1, v / 60);
    return (
      <span style={{
        display: "inline-block", width: 56, padding: "4px 0", textAlign: "center", borderRadius: 6,
        background: `rgba(34,197,94,${0.10 + intensity * 0.55})`,
        color: "var(--text-primary, #0C0C0B)", fontWeight: 700, fontSize: 12,
      }}>{v}%</span>
    );
  };

  return (
    <div className="space-y-4" data-testid="users-retention">
      <SectionHeader title="Retention Analytics" onRefresh={load} />
      {trend && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card data-testid="tile-d7">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-tertiary, #8A8884)", letterSpacing: "0.08em" }}>D7 Retention</div>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 36, fontWeight: 900 }}>{trend.current_d7 ?? "—"}{trend.current_d7 != null ? "%" : ""}</div>
          </Card>
          <Card data-testid="tile-d30">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-tertiary, #8A8884)", letterSpacing: "0.08em" }}>D30 Retention</div>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 36, fontWeight: 900 }}>{trend.current_d30 ?? "—"}{trend.current_d30 != null ? "%" : ""}</div>
          </Card>
        </div>
      )}
      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="retention-table">
          <thead>
            <tr><Th>Cohort week</Th><Th>Size</Th><Th>D7</Th><Th>D14</Th><Th>D21</Th><Th>D30</Th><Th>D60</Th><Th>D90</Th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <Td>{r.cohort_week_start}</Td>
                <Td>{r.cohort_size}</Td>
                <Td>{cell(r.d7)}</Td>
                <Td>{cell(r.d14)}</Td>
                <Td>{cell(r.d21)}</Td>
                <Td>{cell(r.d30)}</Td>
                <Td>{cell(r.d60)}</Td>
                <Td>{cell(r.d90)}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td colSpan={8} style={{ textAlign: "center" }}>No cohorts yet.</Td></tr>}
          </tbody>
        </table>
      </TableShell>
      <p style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
        Each row is a 7-day cohort starting on the listed Sunday. Cells = % of cohort active on or after that day.
      </p>
    </div>
  );
}

export { fmtINR };
